import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Megaphone, AlertCircle, Info, Loader2 } from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const priorityConfig = {
  high: { color: "text-red-600", bg: "bg-red-50", border: "border-red-200", icon: AlertCircle },
  normal: { color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", icon: Info },
  low: { color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200", icon: Info },
};

const LearnerAnnouncementsPage = () => {
  const { user } = useAuth();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const endpoint = user?.role === "manager" ? "/manager/announcements" : "/my/announcements";

  useEffect(() => {
    api.get(endpoint).then(r => setAnnouncements(r.data || [])).catch(console.error).finally(() => setLoading(false));
  }, [endpoint]);

  if (loading) return (
    <AdminLayout title="Announcements" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Announcements" variant={variant}>
      <motion.div className="space-y-5" variants={container} initial="hidden" animate="show" data-testid="announcements-page">
        <motion.div variants={item}>
          <h1 className="text-2xl font-extrabold tracking-tight">Announcements</h1>
          <p className="text-sm text-muted-foreground mt-1">Important updates and notifications</p>
        </motion.div>
        {announcements.length === 0 ? (
          <motion.div variants={item}>
            <Card className="border shadow-none"><CardContent className="p-12 text-center text-muted-foreground"><Megaphone className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No announcements</p></CardContent></Card>
          </motion.div>
        ) : announcements.map((a, i) => {
          const pc = priorityConfig[a.priority] || priorityConfig.normal;
          const PIcon = pc.icon;
          return (
            <motion.div key={a.id || i} variants={item}>
              <Card className={cn("border shadow-none border-l-4", pc.border)} data-testid={`announcement-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5", pc.bg)}><PIcon className={cn("w-4 h-4", pc.color)} /></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-bold">{a.title}</h3>
                        {a.priority === "high" && <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">Important</Badge>}
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{a.content}</p>
                      <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground">
                        <span>By {a.author_name}</span>
                        {a.created_at && <span>{new Date(a.created_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>
    </AdminLayout>
  );
};

export default LearnerAnnouncementsPage;
